/**
 * 文件定位：
 * 1. 当前文件位于 `cad-core/manager/pick` 层；
 * 2. 负责为草图与特征对象提供基于屏幕空间显示尺寸的精确拾取能力；
 * 3. 面命中仍由标准 Raycaster 负责，点/线命中在这里按可见显示尺寸做二次判定。
 */

import * as THREE from 'three/webgpu';
import {
    isFeatureLine,
    isFeaturePoint,
} from '../../mesh';
import {
    isPositionAttributeLike,
    resolveFiniteLinewidth,
    resolveFiniteScaleX,
} from './utils/screenSpacePickRuntimeGuards';
import {
    resolveScreenSpaceLinePickRadiusPx,
    resolveScreenSpacePointPickRadiusPx,
} from './utils/screenSpacePickPolicy';

/**
 * 屏幕空间点拾取输入。
 */
export interface ScreenSpacePointPickInput {
    /** 候选点对象列表。 */
    points: THREE.Sprite[];
    /** 相机。 */
    camera: THREE.Camera;
    /** 容器包围盒。 */
    containerRect: DOMRect;
    /** 客户端 X 坐标。 */
    clientX: number;
    /** 客户端 Y 坐标。 */
    clientY: number;
    /** 当前射线。 */
    ray: THREE.Ray;
}

/**
 * 屏幕空间线拾取输入。
 */
export interface ScreenSpaceLinePickInput {
    /** 候选线对象列表。 */
    lines: THREE.Line[];
    /** 相机。 */
    camera: THREE.Camera;
    /** 容器包围盒。 */
    containerRect: DOMRect;
    /** 客户端 X 坐标。 */
    clientX: number;
    /** 客户端 Y 坐标。 */
    clientY: number;
    /** 当前射线。 */
    ray: THREE.Ray;
}

/**
 * 屏幕空间线命中结果。
 *
 * 设计说明：
 * 1. 选择层在 `face-edge` 模式下需要拿到“当前前景面所有边界线的候选命中”，再结合面命中点做二次裁决；
 * 2. 旧实现只返回单个最近线，导致语义过早收敛，后续无法分辨“当前面边界上的多条可见线”；
 * 3. 因此这里把单条线命中的完整屏幕/世界信息提升为可复用的数据结构，供 selection 层统一消费。
 */
export interface ScreenSpaceLineHit extends THREE.Intersection<THREE.Object3D> {
    /** 屏幕空间距离。 */
    screenDistance: number;
    /** 当前线对象的屏幕拾取半径；字段名保留 visibleHalfWidth 以兼容既有仲裁调用。 */
    visibleHalfWidth: number;
    /** 命中点在线段上的参数。 */
    segmentParameter: number;
}

/**
 * 屏幕空间点命中结果。
 *
 * 设计说明：
 * 1. 草图场景下"重合 foreign point 去噪 + 活动草图点优先"需要候选点集合，而非单值最近点；
 * 2. 因此在 `pickNearestPoint` 之外补一条 `collectPointHits` 路径，供统一选择查询层裁决；
 * 3. `pickNearestPoint` 的现有语义与返回值保持不变，本类型仅面向 `collectPointHits`。
 */
export interface ScreenSpacePointHit extends THREE.Intersection<THREE.Object3D> {
    /** 屏幕空间距离。 */
    screenDistance: number;
    /** 射线距离。 */
    rayDistance: number;
}

/**
 * 屏幕投影结果。
 */
interface ScreenSpaceProjection {
    /** 投影后的屏幕 X 坐标。 */
    clientX: number;
    /** 投影后的屏幕 Y 坐标。 */
    clientY: number;
    /** 投影后的标准化设备坐标 Z。 */
    ndcZ: number;
}

/**
 * 屏幕线段距离结果。
 */
interface ScreenSegmentDistanceResult {
    /** 点到线段的距离。 */
    distance: number;
    /** 最近点在线段上的参数。 */
    t: number;
}

/**
 * 屏幕空间精确拾取器。
 *
 * 设计说明：
 * 1. 草图点/线与特征点/线的拾取都应以真实显示尺寸为准，而不是依赖全局阈值；
 * 2. 该类只负责点 / 线候选的屏幕空间命中计算，不承担任何实体遮挡过滤；
 * 3. 草图与实体之间的最终优先级统一放在 selection 层裁决，避免热路径里混入领域策略。
 */
export class ScreenSpacePrecisionPicker {
    /** 点投影临时向量。 */
    private readonly pointProjection = new THREE.Vector3();
    /** 线起点投影临时向量。 */
    private readonly lineStartProjection = new THREE.Vector3();
    /** 线终点投影临时向量。 */
    private readonly lineEndProjection = new THREE.Vector3();
    /** 线起点临时世界坐标。 */
    private readonly lineStartPoint = new THREE.Vector3();
    /** 线终点临时世界坐标。 */
    private readonly lineEndPoint = new THREE.Vector3();
    /** 点世界坐标临时向量。 */
    private readonly pointWorldPosition = new THREE.Vector3();
    /** 线命中点临时向量。 */
    private readonly lineHitPoint = new THREE.Vector3();
    /** 射线距离计算临时向量。 */
    private readonly rayDistanceVector = new THREE.Vector3();
    /** 点命中的最佳世界坐标缓存。 */
    private readonly bestPointWorld = new THREE.Vector3();
    /** 线命中的最佳世界坐标缓存。 */
    private readonly bestLineWorld = new THREE.Vector3();
    /** 点投影结果缓存。 */
    private readonly pointProjectionResult: ScreenSpaceProjection = {clientX: 0, clientY: 0, ndcZ: 0};
    /** 线起点投影结果缓存。 */
    private readonly lineStartProjectionResult: ScreenSpaceProjection = {clientX: 0, clientY: 0, ndcZ: 0};
    /** 线终点投影结果缓存。 */
    private readonly lineEndProjectionResult: ScreenSpaceProjection = {clientX: 0, clientY: 0, ndcZ: 0};
    /** 屏幕线段距离结果缓存。 */
    private readonly segmentDistanceResult: ScreenSegmentDistanceResult = {distance: 0, t: 0};

    /**
     * 选择最近的点对象命中结果。
     *
     * @param input 点拾取输入。
     * @returns 命中结果；未命中时返回 null。
     */
    public pickNearestPoint(input: ScreenSpacePointPickInput): THREE.Intersection<THREE.Object3D> | null {
        let bestObject: THREE.Sprite | null = null;
        let hasBestPoint = false;
        let bestRayDistance = Number.POSITIVE_INFINITY;
        let bestScreenDistance = Number.POSITIVE_INFINITY;

        input.points.forEach((pointObject) => {
            if (!this.isObjectActuallyVisible(pointObject)) {
                return;
            }

            const worldPoint = this.resolvePointWorldPosition(pointObject, this.pointWorldPosition);
            if (!worldPoint) {
                return;
            }

            const projected = this.projectWorldPointToScreen(
                worldPoint,
                input.camera,
                input.containerRect,
                this.pointProjection,
                this.pointProjectionResult,
            );
            if (!projected || this.pointProjectionResult.ndcZ < -1 || this.pointProjectionResult.ndcZ > 1) {
                return;
            }

            const dx = input.clientX - this.pointProjectionResult.clientX;
            const dy = input.clientY - this.pointProjectionResult.clientY;
            const screenDistance = Math.sqrt(dx * dx + dy * dy);
            const visibleRadius = this.resolvePointVisibleRadiusPx(pointObject);
            if (screenDistance > visibleRadius) {
                return;
            }

            const rayDistance = this.computePointOnRayDistance(input.ray, worldPoint);
            if (rayDistance < 0) {
                return;
            }

            if (
                screenDistance < bestScreenDistance
                || (Math.abs(screenDistance - bestScreenDistance) <= 1e-9 && rayDistance < bestRayDistance)
            ) {
                bestObject = pointObject;
                this.bestPointWorld.copy(worldPoint);
                hasBestPoint = true;
                bestRayDistance = rayDistance;
                bestScreenDistance = screenDistance;
            }
        });

        if (!bestObject || !hasBestPoint) {
            return null;
        }

        return {
            distance: bestRayDistance,
            point: this.bestPointWorld.clone(),
            object: bestObject,
            screenDistance: bestScreenDistance,
        } as THREE.Intersection<THREE.Object3D>;
    }

    /**
     * 收集所有满足命中阈值的点命中结果。
     *
     * 设计说明：
     * 1. 与 `pickNearestPoint` 共享完全一致的 "屏幕距离 + 射线距离" 判定逻辑，只是返回候选集合；
     * 2. 排序规则：先按 `screenDistance` 升序，再按 `rayDistance` 升序——与 `pickNearestPoint` 的 tie-break 一致；
     * 3. 不做"活动草图优先 / foreign point 去噪"这类领域裁决，领域层在统一查询 / 仲裁层消费候选集合。
     *
     * @param input 点拾取输入。
     * @returns 命中结果集合，已按 (screenDistance, rayDistance) 升序排序。
     */
    public collectPointHits(input: ScreenSpacePointPickInput): ScreenSpacePointHit[] {
        const hits: ScreenSpacePointHit[] = [];

        input.points.forEach((pointObject) => {
            if (!this.isObjectActuallyVisible(pointObject)) {
                return;
            }

            const worldPoint = this.resolvePointWorldPosition(pointObject, this.pointWorldPosition);
            if (!worldPoint) {
                return;
            }

            const projected = this.projectWorldPointToScreen(
                worldPoint,
                input.camera,
                input.containerRect,
                this.pointProjection,
                this.pointProjectionResult,
            );
            if (!projected || this.pointProjectionResult.ndcZ < -1 || this.pointProjectionResult.ndcZ > 1) {
                return;
            }

            const dx = input.clientX - this.pointProjectionResult.clientX;
            const dy = input.clientY - this.pointProjectionResult.clientY;
            const screenDistance = Math.sqrt(dx * dx + dy * dy);
            const visibleRadius = this.resolvePointVisibleRadiusPx(pointObject);
            if (screenDistance > visibleRadius) {
                return;
            }

            const rayDistance = this.computePointOnRayDistance(input.ray, worldPoint);
            if (rayDistance < 0) {
                return;
            }

            hits.push({
                distance: rayDistance,
                point: worldPoint.clone(),
                object: pointObject,
                screenDistance,
                rayDistance,
            } as ScreenSpacePointHit);
        });

        hits.sort((a, b) => {
            if (Math.abs(a.screenDistance - b.screenDistance) > 1e-9) {
                return a.screenDistance - b.screenDistance;
            }
            return a.rayDistance - b.rayDistance;
        });

        return hits;
    }

    /**
     * 选择最近的线对象命中结果。
     *
     * @param input 线拾取输入。
     * @returns 命中结果；未命中时返回 null。
     */
    public pickNearestLine(input: ScreenSpaceLinePickInput): THREE.Intersection<THREE.Object3D> | null {
        const hits = this.collectLineHits(input);
        if (hits.length === 0) {
            return null;
        }

        return hits.reduce<ScreenSpaceLineHit | null>((bestHit, currentHit) => {
            if (!bestHit) {
                return currentHit;
            }
            if (currentHit.screenDistance < bestHit.screenDistance) {
                return currentHit;
            }
            if (
                Math.abs(currentHit.screenDistance - bestHit.screenDistance) <= 1e-9
                && currentHit.distance < bestHit.distance
            ) {
                return currentHit;
            }
            return bestHit;
        }, null);
    }

    /**
     * 收集所有满足命中阈值的线命中结果。
     *
     * @param input 线拾取输入。
     * @returns 命中结果集合。
     */
    public collectLineHits(input: ScreenSpaceLinePickInput): ScreenSpaceLineHit[] {
        const hits: ScreenSpaceLineHit[] = [];

        input.lines.forEach((lineObject) => {
            if (!this.isObjectActuallyVisible(lineObject)) {
                return;
            }

            const positionAttribute = lineObject.geometry.getAttribute('position');
            if (!isPositionAttributeLike(positionAttribute) || positionAttribute.count < 2) {
                return;
            }

            const pickRadiusPx = this.resolveLinePickRadiusPx(lineObject);
            //------ 基准参考元素屏幕空间像素拾取 开始------
            const isSegments = (lineObject as any).isLineSegments === true;
            const step = isSegments ? 2 : 1;
            for (let index = 0; index < positionAttribute.count - 1; index += step) {
            //------ 基准参考元素屏幕空间像素拾取 结束------
                this.lineStartPoint.set(
                    positionAttribute.getX(index),
                    positionAttribute.getY(index),
                    positionAttribute.getZ(index),
                ).applyMatrix4(lineObject.matrixWorld);
                this.lineEndPoint.set(
                    positionAttribute.getX(index + 1),
                    positionAttribute.getY(index + 1),
                    positionAttribute.getZ(index + 1),
                ).applyMatrix4(lineObject.matrixWorld);

                const hasScreenStart = this.projectWorldPointToScreen(
                    this.lineStartPoint,
                    input.camera,
                    input.containerRect,
                    this.lineStartProjection,
                    this.lineStartProjectionResult,
                );
                const hasScreenEnd = this.projectWorldPointToScreen(
                    this.lineEndPoint,
                    input.camera,
                    input.containerRect,
                    this.lineEndProjection,
                    this.lineEndProjectionResult,
                );
                if (!hasScreenStart || !hasScreenEnd) {
                    continue;
                }

                if (
                    (this.lineStartProjectionResult.ndcZ < -1 || this.lineStartProjectionResult.ndcZ > 1)
                    && (this.lineEndProjectionResult.ndcZ < -1 || this.lineEndProjectionResult.ndcZ > 1)
                ) {
                    continue;
                }

                const distanceResult = this.computeDistanceToScreenSegment(
                    input.clientX,
                    input.clientY,
                    this.lineStartProjectionResult.clientX,
                    this.lineStartProjectionResult.clientY,
                    this.lineEndProjectionResult.clientX,
                    this.lineEndProjectionResult.clientY,
                );
                if (distanceResult.distance > pickRadiusPx) {
                    continue;
                }

                const worldPoint = this.lineHitPoint.copy(this.lineStartPoint).lerp(this.lineEndPoint, distanceResult.t);
                const rayDistance = this.computePointOnRayDistance(input.ray, worldPoint);
                if (rayDistance < 0) {
                    continue;
                }
                this.bestLineWorld.copy(worldPoint);
                hits.push({
                    distance: rayDistance,
                    point: this.bestLineWorld.clone(),
                    object: lineObject,
                    screenDistance: distanceResult.distance,
                    visibleHalfWidth: pickRadiusPx,
                    segmentParameter: distanceResult.t,
                } as ScreenSpaceLineHit);
            }
        });

        return hits;
    }

    /**
     * 判断对象是否真实可见。
     *
     * @param object 目标对象。
     * @returns 是否可见。
     */
    private isObjectActuallyVisible(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (!current.visible) {
                /**
                 * 特征点在部分链路里会把可见性挂在父对象上，
                 * 这里继续保留原有兼容逻辑，避免误判。
                 */
                if (current === object && isFeaturePoint(object)) {
                    current = current.parent;
                    continue;
                }

                return false;
            }
            current = current.parent;
        }

        return true;
    }

    /**
     * 解析点对象的世界坐标。
     *
     * @param pointObject 点对象。
     * @param target 目标向量。
     * @returns 世界坐标；无法解析时返回 null。
     */
    private resolvePointWorldPosition(pointObject: THREE.Sprite, target: THREE.Vector3): THREE.Vector3 | null {
        return pointObject.getWorldPosition(target);
    }

    /**
     * 解析点对象的可视半径（像素）。
     *
     * @param pointObject 点对象。
     * @returns 可视半径。
     */
    private resolvePointVisibleRadiusPx(pointObject: THREE.Sprite): number {
        const scaleX = resolveFiniteScaleX(pointObject);
        return resolveScreenSpacePointPickRadiusPx(isFeaturePoint(pointObject), scaleX);
    }

    /**
     * 解析线对象的屏幕拾取半径（像素）。
     *
     * @param lineObject 线对象。
     * @returns 屏幕拾取半径。
     */
    private resolveLinePickRadiusPx(lineObject: THREE.Line): number {
        const material = Array.isArray(lineObject.material) ? lineObject.material[0] : lineObject.material;
        const linewidth = resolveFiniteLinewidth(material);
        return resolveScreenSpaceLinePickRadiusPx(isFeatureLine(lineObject), linewidth);
    }

    /**
     * 将世界坐标点投影到屏幕。
     *
     * @param worldPoint 世界坐标。
     * @param camera 相机。
     * @param containerRect 容器包围盒。
     * @param target 目标向量。
     * @param result 输出投影结果。
     * @returns 是否投影成功。
     */
    private projectWorldPointToScreen(
        worldPoint: THREE.Vector3,
        camera: THREE.Camera,
        containerRect: DOMRect,
        target: THREE.Vector3,
        result: ScreenSpaceProjection,
    ): boolean {
        const projected = target.copy(worldPoint).project(camera);
        if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y) || !Number.isFinite(projected.z)) {
            return false;
        }

        result.clientX = containerRect.left + ((projected.x + 1) * 0.5) * containerRect.width;
        result.clientY = containerRect.top + ((1 - projected.y) * 0.5) * containerRect.height;
        result.ndcZ = projected.z;
        return true;
    }

    /**
     * 计算点在当前射线方向上的投影距离。
     *
     * @param ray 射线。
     * @param point 点坐标。
     * @returns 投影距离。
     */
    private computePointOnRayDistance(ray: THREE.Ray, point: THREE.Vector3): number {
        return this.rayDistanceVector.copy(point).sub(ray.origin).dot(ray.direction);
    }

    /**
     * 计算屏幕点到屏幕线段的距离。
     *
     * @returns 距离与参数 t。
     * @param pointX
     * @param pointY
     * @param segmentStartX
     * @param segmentStartY
     * @param segmentEndX
     * @param segmentEndY
     */
    private computeDistanceToScreenSegment(
        pointX: number,
        pointY: number,
        segmentStartX: number,
        segmentStartY: number,
        segmentEndX: number,
        segmentEndY: number,
    ): ScreenSegmentDistanceResult {
        const abX = segmentEndX - segmentStartX;
        const abY = segmentEndY - segmentStartY;
        const abLengthSquared = abX * abX + abY * abY;
        if (abLengthSquared < 1e-12) {
            const dx = pointX - segmentStartX;
            const dy = pointY - segmentStartY;
            this.segmentDistanceResult.distance = Math.sqrt(dx * dx + dy * dy);
            this.segmentDistanceResult.t = 0;
            return this.segmentDistanceResult;
        }

        const apX = pointX - segmentStartX;
        const apY = pointY - segmentStartY;
        const t = THREE.MathUtils.clamp((apX * abX + apY * abY) / abLengthSquared, 0, 1);
        const closestX = segmentStartX + abX * t;
        const closestY = segmentStartY + abY * t;
        const dx = pointX - closestX;
        const dy = pointY - closestY;
        this.segmentDistanceResult.distance = Math.sqrt(dx * dx + dy * dy);
        this.segmentDistanceResult.t = t;
        return this.segmentDistanceResult;
    }
}
